/**
 * Static Fields Configuration
 *
 * targetType values:
 * - EMPLOYEE: Only shown for employee services
 * - ORGANIZATION: Only shown for organization services
 * - COMMON: Shown for all service types
 *
 * type values (field input types):
 * - TEXT: Text input field
 * - DATE: Date picker (calendar)
 * - FILE: File upload
 * - EMAIL: Email input with validation
 * - PHONE: Phone number input
 * - SELECT: Dropdown selection
 * - TEXTAREA: Multi-line text area
 * - NUMBER: Numeric input
 *
 * category values (for UI grouping):
 * - PERSONAL_INFO, IDENTIFICATION, CONTACT_INFO, ADDRESS, DOCUMENTS, etc.
 */

// ============================================
// FIELD TYPES ENUM (for reference)
// ============================================
export const FIELD_TYPES = {
  TEXT: 'TEXT',
  DATE: 'DATE',
  FILE: 'FILE',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  SELECT: 'SELECT',
  TEXTAREA: 'TEXTAREA',
  NUMBER: 'NUMBER',
};

// ============================================
// COMMON FIELDS (Applicable to both Employee & Organization)
// ============================================
const COMMON_FIELDS = {
  // Generic Date Fields (Reusable for any service)
  issueDate: {
    label: "Issue Date",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "DATE",
  },
  expiryDate: {
    label: "Expiry Date",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "DATE",
  },
  validFrom: {
    label: "Valid From",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "DATE",
  },
  validTo: {
    label: "Valid To",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "DATE",
  },

  // Contact Information
  email: {
    label: "Email",
    targetType: "COMMON",
    category: "CONTACT_INFO",
    type: "EMAIL",
  },
  mobileNumber: {
    label: "Mobile Number",
    targetType: "COMMON",
    category: "CONTACT_INFO",
    type: "PHONE",
  },
  landNumber: {
    label: "Landline Number",
    targetType: "COMMON",
    category: "CONTACT_INFO",
    type: "PHONE",
  },
  faxNumber: {
    label: "Fax Number",
    targetType: "COMMON",
    category: "CONTACT_INFO",
    type: "PHONE",
  },

  // Address
  emirate: {
    label: "Emirate",
    targetType: "COMMON",
    category: "ADDRESS",
    type: "SELECT",
  },
  area: {
    label: "Area",
    targetType: "COMMON",
    category: "ADDRESS",
    type: "TEXT",
  },
  address: {
    label: "Address",
    targetType: "COMMON",
    category: "ADDRESS",
    type: "TEXTAREA",
  },
  poBox: {
    label: "PO Box",
    targetType: "COMMON",
    category: "ADDRESS",
    type: "TEXT",
  },

  // Service Specific
  fileNumber: {
    label: "File Number",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "TEXT",
  },
  referenceNumber: {
    label: "Reference Number",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "TEXT",
  },
  documentNumber: {
    label: "Document Number",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "TEXT",
  },
  documentCopy: {
    label: "Document Copy",
    targetType: "COMMON",
    category: "SERVICE_SPECIFIC",
    type: "FILE",
  },

  // Other
  remarks: {
    label: "Remarks/Notes",
    targetType: "COMMON",
    category: "OTHER",
    type: "TEXTAREA",
  },
};

// ============================================
// EMPLOYEE FIELDS (Only for Employee Services)
// ============================================
const EMPLOYEE_FIELDS = {
  // Personal Information
  firstNameEn: {
    label: "First Name (English)",
    checked: true,
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  middleNameEn: {
    label: "Middle Name (English)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  lastNameEn: {
    label: "Last Name (English)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  firstNameAr: {
    label: "First Name (Arabic)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  middleNameAr: {
    label: "Middle Name (Arabic)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  lastNameAr: {
    label: "Last Name (Arabic)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  nameEn: {
    label: "Full Name (English)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  nameAr: {
    label: "Full Name (Arabic)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  dob: {
    label: "Date Of Birth",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "DATE",
  },
  gender: {
    label: "Gender",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "SELECT",
  },
  nationality: {
    label: "Nationality",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "SELECT",
  },
  religion: {
    label: "Religion",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "SELECT",
  },
  maritalStatus: {
    label: "Marital Status",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "SELECT",
  },

  // Birth Information
  birthPlaceEn: {
    label: "Birth Place (English)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  birthPlaceAr: {
    label: "Birth Place (Arabic)",
    targetType: "EMPLOYEE",
    category: "PERSONAL_INFO",
    type: "TEXT",
  },
  birthCertificate: {
    label: "Birth Certificate",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },

  // Passport Information
  passportNumber: {
    label: "Passport Number",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "TEXT",
  },
  passportIssue: {
    label: "Passport Issue Date",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "DATE",
  },
  passportExpiry: {
    label: "Passport Expiry Date",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "DATE",
  },
  passportIssuePlaceEn: {
    label: "Passport Issue Place (English)",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "TEXT",
  },
  passportIssuePlaceAr: {
    label: "Passport Issue Place (Arabic)",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "TEXT",
  },
  passportCopy: {
    label: "Passport Copy",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },

  // Emirates ID Information
  emiratesId: {
    label: "Emirates ID Number",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "TEXT",
  },
  emiratesIdIssueDate: {
    label: "Emirates ID Issue Date",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "DATE",
  },
  emiratesIdExpiryDate: {
    label: "Emirates ID Expiry Date",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "DATE",
  },
  emiratesIdCopy: {
    label: "Emirates ID Copy",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },

  // Visa Information
  visaNumber: {
    label: "Visa Number",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "TEXT",
  },
  visaType: {
    label: "Visa Type",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "SELECT",
  },
  visaIssueDate: {
    label: "Visa Issue Date",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "DATE",
  },
  visaExpiryDate: {
    label: "Visa Expiry Date",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "DATE",
  },
  visaSponsorName: {
    label: "Visa Sponsor Name",
    targetType: "EMPLOYEE",
    category: "IDENTIFICATION",
    type: "TEXT",
  },
  visaPage: {
    label: "Visa Page Copy",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },

  // Address (Employee Specific)
  buildingNumber: {
    label: "Building Number",
    targetType: "EMPLOYEE",
    category: "ADDRESS",
    type: "TEXT",
  },
  deliveryNumber: {
    label: "Delivery Number",
    targetType: "EMPLOYEE",
    category: "ADDRESS",
    type: "TEXT",
  },

  // Other Employee Fields
  photo: {
    label: "Photo",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  shinasnama: {
    label: "Shinsnama",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  labourContract: {
    label: "Labour Contract",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  salaryCertificate: {
    label: "Salary Certificate",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  resume: {
    label: "Resume / CV",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  educationalCertificate: {
    label: "Educational Certificate",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  experienceCertificate: {
    label: "Experience Certificate",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },
  policeClearanceCertificate: {
    label: "Police Clearance Certificate",
    targetType: "EMPLOYEE",
    category: "DOCUMENTS",
    type: "FILE",
  },

  // Location Preferences
  biometricLocation: {
    label: "Biometric Location",
    targetType: "EMPLOYEE",
    category: "LOCATIONS",
    type: "SELECT",
  },
  medicalLocation: {
    label: "Medical Location",
    targetType: "EMPLOYEE",
    category: "LOCATIONS",
    type: "SELECT",
  },
  ohhLocation: {
    label: "OHH Location",
    targetType: "EMPLOYEE",
    category: "LOCATIONS",
    type: "SELECT",
  },
};

// ============================================
// ORGANIZATION FIELDS (Only for Organization Services)
// ============================================
const ORGANIZATION_FIELDS = {
  // Company Information
  companyNameEn: {
    label: "Company Name (English)",
    checked: true,
    targetType: "ORGANIZATION",
    category: "COMPANY_INFO",
    type: "TEXT",
  },
  companyNameAr: {
    label: "Company Name (Arabic)",
    targetType: "ORGANIZATION",
    category: "COMPANY_INFO",
    type: "TEXT",
  },
  legalForm: {
    label: "Legal Form (LLC, FZ-LLC, etc.)",
    targetType: "ORGANIZATION",
    category: "COMPANY_INFO",
    type: "SELECT",
  },
  establishmentDate: {
    label: "Establishment Date",
    targetType: "ORGANIZATION",
    category: "COMPANY_INFO",
    type: "DATE",
  },

  // License & Registration
  licenseNumber: {
    label: "Trade License Number",
    targetType: "ORGANIZATION",
    category: "LICENSE_INFO",
    type: "TEXT",
  },
  licenseIssueDate: {
    label: "License Issue Date",
    targetType: "ORGANIZATION",
    category: "LICENSE_INFO",
    type: "DATE",
  },
  licenseExpiryDate: {
    label: "License Expiry Date",
    targetType: "ORGANIZATION",
    category: "LICENSE_INFO",
    type: "DATE",
  },
  licenseAuthority: {
    label: "License Issuing Authority",
    targetType: "ORGANIZATION",
    category: "LICENSE_INFO",
    type: "SELECT",
  },
  commercialRegistration: {
    label: "Commercial Registration Number",
    targetType: "ORGANIZATION",
    category: "LICENSE_INFO",
    type: "TEXT",
  },

  // Government Cards
  establishmentCardNumber: {
    label: "Establishment Card Number",
    targetType: "ORGANIZATION",
    category: "GOVERNMENT_CARDS",
    type: "TEXT",
  },
  establishmentCardExpiry: {
    label: "Establishment Card Expiry",
    targetType: "ORGANIZATION",
    category: "GOVERNMENT_CARDS",
    type: "DATE",
  },
  immigrationCardNumber: {
    label: "Immigration Card Number",
    targetType: "ORGANIZATION",
    category: "GOVERNMENT_CARDS",
    type: "TEXT",
  },
  immigrationCardExpiry: {
    label: "Immigration Card Expiry",
    targetType: "ORGANIZATION",
    category: "GOVERNMENT_CARDS",
    type: "DATE",
  },
  mohreLicenseNumber: {
    label: "MOHRE License Number",
    targetType: "ORGANIZATION",
    category: "GOVERNMENT_CARDS",
    type: "TEXT",
  },
  chamberMembershipNumber: {
    label: "Chamber Membership Number",
    targetType: "ORGANIZATION",
    category: "GOVERNMENT_CARDS",
    type: "TEXT",
  },

  // Tax Information
  trnNumber: {
    label: "Tax Registration Number (TRN)",
    targetType: "ORGANIZATION",
    category: "TAX_INFO",
    type: "TEXT",
  },
  vatRegistrationNumber: {
    label: "VAT Registration Number",
    targetType: "ORGANIZATION",
    category: "TAX_INFO",
    type: "TEXT",
  },
  vatRegistrationDate: {
    label: "VAT Registration Date",
    targetType: "ORGANIZATION",
    category: "TAX_INFO",
    type: "DATE",
  },
  corporateTaxRegistration: {
    label: "Corporate Tax Registration",
    targetType: "ORGANIZATION",
    category: "TAX_INFO",
    type: "TEXT",
  },
  taxYear: {
    label: "Tax Year",
    targetType: "ORGANIZATION",
    category: "TAX_INFO",
    type: "SELECT",
  },

  // Bank Information
  bankName: {
    label: "Bank Name",
    targetType: "ORGANIZATION",
    category: "FINANCIAL",
    type: "SELECT",
  },
  accountNumber: {
    label: "Bank Account Number",
    targetType: "ORGANIZATION",
    category: "FINANCIAL",
    type: "TEXT",
  },
  iban: {
    label: "IBAN",
    targetType: "ORGANIZATION",
    category: "FINANCIAL",
    type: "TEXT",
  },
  swiftCode: {
    label: "SWIFT Code",
    targetType: "ORGANIZATION",
    category: "FINANCIAL",
    type: "TEXT",
  },

  // Authorized Signatory
  authSignatoryName: {
    label: "Authorized Signatory Name",
    targetType: "ORGANIZATION",
    category: "SIGNATORY",
    type: "TEXT",
  },
  authSignatoryDesignation: {
    label: "Signatory Designation",
    targetType: "ORGANIZATION",
    category: "SIGNATORY",
    type: "TEXT",
  },
  authSignatoryEmiratesId: {
    label: "Signatory Emirates ID",
    targetType: "ORGANIZATION",
    category: "SIGNATORY",
    type: "TEXT",
  },
  authSignatoryMobile: {
    label: "Signatory Mobile",
    targetType: "ORGANIZATION",
    category: "SIGNATORY",
    type: "PHONE",
  },
  authSignatoryEmail: {
    label: "Signatory Email",
    targetType: "ORGANIZATION",
    category: "SIGNATORY",
    type: "EMAIL",
  },

  // Owner Information
  ownerName: {
    label: "Owner Name",
    targetType: "ORGANIZATION",
    category: "OWNER_INFO",
    type: "TEXT",
  },
  ownerNationality: {
    label: "Owner Nationality",
    targetType: "ORGANIZATION",
    category: "OWNER_INFO",
    type: "SELECT",
  },
  ownerPassportNumber: {
    label: "Owner Passport Number",
    targetType: "ORGANIZATION",
    category: "OWNER_INFO",
    type: "TEXT",
  },
  ownerEmiratesId: {
    label: "Owner Emirates ID",
    targetType: "ORGANIZATION",
    category: "OWNER_INFO",
    type: "TEXT",
  },
  ownerSharePercentage: {
    label: "Owner Share %",
    targetType: "ORGANIZATION",
    category: "OWNER_INFO",
    type: "NUMBER",
  },

  // Organization Documents
  tradeLicenseCopy: {
    label: "Trade License Copy",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  memoOfAssociation: {
    label: "Memorandum of Association",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  articleOfAssociation: {
    label: "Article of Association",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  establishmentCardCopy: {
    label: "Establishment Card Copy",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  immigrationCardCopy: {
    label: "Immigration Card Copy",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  chamberCertificateCopy: {
    label: "Chamber Certificate Copy",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  vatCertificateCopy: {
    label: "VAT Certificate Copy",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  trnCertificateCopy: {
    label: "TRN Certificate Copy",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  shareCapitalCertificate: {
    label: "Share Capital Certificate",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  powerOfAttorney: {
    label: "Power of Attorney",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  boardResolution: {
    label: "Board Resolution",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
  auditedFinancials: {
    label: "Audited Financial Statements",
    targetType: "ORGANIZATION",
    category: "ORG_DOCUMENTS",
    type: "FILE",
  },
};

// ============================================
// COMBINED STATIC FIELDS (Export)
// ============================================
export const STATIC_FIELDS = {
  ...COMMON_FIELDS,
  ...EMPLOYEE_FIELDS,
  ...ORGANIZATION_FIELDS,
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get fields filtered by target type
 * @param {string} targetType - EMPLOYEE, ORGANIZATION, or BOTH
 * @returns {Object} Filtered fields
 */
export const getFieldsByTargetType = (targetType) => {
  const result = {};

  Object.keys(STATIC_FIELDS).forEach(key => {
    const field = STATIC_FIELDS[key];
    const fieldTargetType = field.targetType || 'EMPLOYEE';

    // COMMON fields always included
    if (fieldTargetType === 'COMMON') {
      result[key] = field;
      return;
    }

    // Include based on target type
    if (targetType === 'BOTH') {
      result[key] = field;
    } else if (targetType === fieldTargetType) {
      result[key] = field;
    }
  });

  return result;
};

/**
 * Get category display names for organization fields
 */
export const ORG_CATEGORY_DISPLAY_NAMES = {
  'COMPANY_INFO': 'Company Information',
  'LICENSE_INFO': 'License & Registration',
  'GOVERNMENT_CARDS': 'Government Cards',
  'TAX_INFO': 'Tax Information',
  'FINANCIAL': 'Bank & Financial',
  'SIGNATORY': 'Authorized Signatory',
  'OWNER_INFO': 'Owner Information',
  'ORG_DOCUMENTS': 'Organization Documents',
};

/**
 * Get all category display names (merged)
 */
export const ALL_CATEGORY_DISPLAY_NAMES = {
  // Common
  'CONTACT_INFO': 'Contact Information',
  'ADDRESS': 'Address',
  'SERVICE_SPECIFIC': 'Service Specific',
  'OTHER': 'Other',

  // Employee
  'PERSONAL_INFO': 'Personal Information',
  'IDENTIFICATION': 'Identification',
  'DOCUMENTS': 'Documents',
  'LOCATIONS': 'Locations',

  // Organization
  ...ORG_CATEGORY_DISPLAY_NAMES,
};

// For backwards compatibility - original structure
export default STATIC_FIELDS;
